// OpenAI API integration for AI Assistant
// Uses GPT-4-turbo for intelligent summarization and Q&A
// ⚠️ OpenAI API calls are now handled by Supabase Edge Function for security

import { supabase } from '@/lib/supabase';

export interface AIServiceConfig {
  model?: string;
  temperature?: number;
  maxTokens?: number;
}

const DEFAULT_CONFIG: AIServiceConfig = {
  model: 'gpt-4-turbo',
  temperature: 0.3,
  maxTokens: 500,
};

/**
 * Generate a concise summary of the discussion
 */
export async function generateSummary(
  transcript: string,
  context?: string,
  organizerContext?: {
    assignments?: Array<{ title: string; dueDate: string; status: string }>;
    upcomingDeadlines?: string[];
  }
): Promise<string> {
  try {
    let contextPrompt = context ? `\nContext: ${context}` : '';
    
    // Add organizer context to help AI provide relevant summaries
    if (organizerContext) {
      if (organizerContext.upcomingDeadlines && organizerContext.upcomingDeadlines.length > 0) {
        contextPrompt += `\n\nUpcoming Deadlines: ${organizerContext.upcomingDeadlines.join(', ')}`;
      }
      if (organizerContext.assignments && organizerContext.assignments.length > 0) {
        const relevantAssignments = organizerContext.assignments
          .filter(a => a.status !== 'completed')
          .slice(0, 3);
        if (relevantAssignments.length > 0) {
          contextPrompt += `\n\nActive Assignments: ${relevantAssignments.map(a => 
            `${a.title} (Due: ${a.dueDate})`
          ).join(', ')}`;
        }
      }
    }

    const systemPrompt = `You are an AI assistant helping students in academic study groups. 
Analyze discussions and provide clear, concise summaries focused on learning outcomes.
Keep summaries under 100 words and use academic language.`;

    const userPrompt = `Summarize this study group discussion:\n\n${transcript}${contextPrompt}`;

    // Call secure Edge Function instead of OpenAI directly
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return 'Please sign in to use AI features.';
    }

    const response = await supabase.functions.invoke('whipt-ai-summary', {
      body: {
        text: transcript + contextPrompt,
        prompt: systemPrompt,
        model: DEFAULT_CONFIG.model,
      },
    });

    if (response.error) {
      throw new Error(response.error.message);
    }

    return response.data?.summary || 'Unable to generate summary.';
  } catch (error) {
    console.error('[AI Service] Error generating summary:', error);
    return 'Error generating AI summary. Please try again.';
  }
}

/**
 * Extract key points from the discussion
 * ⚠️ DISABLED - OpenAI key should not be in client code
 * Use Supabase Edge Function instead
 */
export async function extractKeyPoints(
  transcript: string
): Promise<string[]> {
  console.warn('[AI Service] extractKeyPoints is disabled for security. Implement via Edge Function if needed.');
  return [];
}

/**
 * Detect action items with owners and deadlines
 * ⚠️ DISABLED - OpenAI key should not be in client code
 * Use Supabase Edge Function instead
 */
export async function detectActionItems(
  transcript: string
): Promise<Array<{ owner: string; task: string; deadline?: string }>> {
  console.warn('[AI Service] detectActionItems is disabled for security. Implement via Edge Function if needed.');
  return [];
}

/**
 * Answer questions based on the discussion context
 */
export async function answerQuestion(
  question: string,
  transcript: string,
  chatHistory: string[]
): Promise<string> {
  try {
    const context = `
Discussion Transcript:
${transcript}

Recent Chat Messages:
${chatHistory.slice(-10).join('\n')}
    `.trim();

    // Use Supabase Edge Function instead of direct OpenAI call (avoids CORS)
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    
    if (!supabaseUrl || !supabaseKey) {
      return 'AI services not configured. Please contact support.';
    }

    const response = await fetch(`${supabaseUrl}/functions/v1/ai-assist`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseKey}`,
        'apikey': supabaseKey,
      },
      body: JSON.stringify({
        mode: 'chat',
        payload: {
          conversation: [
            {
              role: 'user',
              content: `Based on this context:\n\n${context}\n\nQuestion: ${question}`,
            },
          ],
          contextSummary: 'Virtual room discussion assistant helping students understand their study group conversation.',
        },
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[AI Service] Edge function error:', response.status, errorText);
      throw new Error(`AI service error: ${response.status} - ${errorText}`);
    }

    // Check if response has content before parsing JSON
    const responseText = await response.text();
    if (!responseText || responseText.trim() === '') {
      console.error('[AI Service] Empty response from Edge function');
      throw new Error('Empty response from AI service');
    }

    let data;
    try {
      data = JSON.parse(responseText);
    } catch (parseError) {
      console.error('[AI Service] JSON parse error:', parseError);
      console.error('[AI Service] Response text:', responseText.substring(0, 200));
      throw new Error('Invalid response format from AI service');
    }

    return data.result?.reply || data.result?.answer || data.result?.message || 'Unable to answer the question.';
  } catch (error) {
    console.error('[AI Service] Error answering question:', error);
    console.error('[AI Service] Error details:', error instanceof Error ? error.message : error);
    return 'Error processing your question. Please try again.';
  }
}

/**
 * Check if OpenAI API is configured
 * Always returns true now - Edge Functions handle API keys server-side
 */
export function isAIConfigured(): boolean {
  return true; // Server-side via Edge Functions
}

/**
 * Get estimated cost for a request (GPT-4-turbo pricing)
 */
export function estimateCost(inputTokens: number, outputTokens: number): number {
  const INPUT_COST = 0.01 / 1000; // $0.01 per 1K tokens
  const OUTPUT_COST = 0.03 / 1000; // $0.03 per 1K tokens
  return (inputTokens * INPUT_COST) + (outputTokens * OUTPUT_COST);
}
